import type { ReactElement } from 'react';
import { useState, useRef, useEffect } from 'react';
import { Component as CheckIcon } from 'icons/icon-ui--check.svg';

type Props = {
  id: string;
  leftLabel: string;
  rightLabel: string;
  name: string;
  steps: string[];
  onChange: (value: string) => void;
};

function RangeSlider({
  id,
  leftLabel,
  rightLabel,
  name,
  steps,
  onChange,
}: Props): ReactElement {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [leftValue, setLeftValue] = useState(null);
  const sliderRef = useRef<HTMLDivElement>();
  const stepsRef = useRef<HTMLSpanElement[]>([]);

  useEffect(() => {
    let rect;
    let firstStepLeftValue;
    let leftValues;

    const onWindowResize = () => {
      rect = (sliderRef.current as HTMLDivElement).getBoundingClientRect();
      firstStepLeftValue = stepsRef.current[0].getBoundingClientRect().left;

      leftValues = stepsRef.current.map(
        (step) => step.getBoundingClientRect().left - firstStepLeftValue,
      );
      // @ts-expect-error TODO: fix null safety
      if (leftValue !== null) setLeftValue(leftValues[selectedIndex]);
    };

    onWindowResize();

    const getClosestIndex = (values, currentValue) => {
      let closestIndex = 0;
      let closestValue = values[0];

      values.forEach((value, index) => {
        if (
          Math.abs(currentValue - value)
          < Math.abs(currentValue - closestValue)
        ) {
          closestIndex = index;
          closestValue = value;
        }
      });

      return closestIndex;
    };

    const onMouseClick = (e) => {
      const x = e.clientX - rect.left;
      const closestIndex = getClosestIndex(leftValues, x);

      if (closestIndex !== selectedIndex) {
        // @ts-expect-error TODO: fix null safety
        setSelectedIndex(closestIndex);
        setLeftValue(leftValues[closestIndex]);
        onChange(steps[closestIndex]);
      }
    };

    const onMouseMove = (e) => {
      if (e.buttons === 1) onMouseClick(e);
    };

    window.addEventListener('resize', onWindowResize);
    // @ts-expect-error TODO: fix null safety
    sliderRef.current.addEventListener('mousemove', onMouseMove);
    // @ts-expect-error TODO: fix null safety
    sliderRef.current.addEventListener('click', onMouseClick);

    const cachedSliderRef = sliderRef.current;
    return () => {
      window.removeEventListener('resize', onWindowResize);
      // @ts-expect-error TODO: fix null safety
      cachedSliderRef.removeEventListener('mousemove', onMouseMove);
      // @ts-expect-error TODO: fix null safetys
      cachedSliderRef.removeEventListener('click', onMouseClick);
    };
  }, [onChange, selectedIndex, steps, leftValue]);

  return (
    <div
      className={[
        'c-input-range-wrapper',
        selectedIndex !== null ? 'is-activated' : '',
      ].join(' ')}
        // @ts-expect-error TODO: fix null safety
      ref={sliderRef}
    >
      <input
        type="range"
        min={steps[0]}
        max={steps[steps.length - 1]}
          // @ts-expect-error TODO: fix null safety
        value={steps[selectedIndex] || ''}
        onChange={() => null}
        name={name}
        id={id}
      />

      <div
        className={[
          'c-input-range',
          'rangeslider',
          'c-input-range--horizontal',
          'rangeslider--horizontal',
        ].join(' ')}
      >
        <div
          className="c-input-range--fill rangeslider__fill"
          style={{
            width: selectedIndex === 0
              ? '0px'
            // @ts-expect-error TODO: fix null safety
              : `${leftValue + 10}px`,
          }}
        />

        <div
          className="c-input-range__handle"
          style={{ left: `${leftValue}px` }}
        >
          <CheckIcon width="12.7" height="9.5" />
        </div>
      </div>

      <div className="c-input-range__ticks">
        {steps.map((step, i) => (
          <span
            key={step}
              // @ts-expect-error TODO: fix null safety
            ref={(el) => { stepsRef.current[i] = el; }}
            className={[
              'c-input-range__tick',
              selectedIndex === i ? 'is-selected' : '',
            ].join(' ')}
          >
            <span className="c-input-range__tick-value">
              {step}
            </span>
            {i === 0 && (
              <span className="c-input-range__tick-label">
                {leftLabel}
              </span>
            )}
            {i === steps.length - 1 && (
              <span className="c-input-range__tick-label">
                {rightLabel}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

export default RangeSlider;
